import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, IconButton, Dialog, DialogContent, TextField, FormControlLabel, Switch, Chip, Checkbox, FormControl, InputLabel, Select, MenuItem, OutlinedInput, ListItemText, Snackbar, Alert, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import NfcIcon from '@mui/icons-material/Nfc';
import supabase from '../../../supabaseClient';

const emptyForm = {
    name: '',
    surname: '',
    position: '',
    contract_type: '',
    hourly_rate: '',
    pin: '',
    nfc_card_id: '',
};

function ManagerFilesWorker() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [idConfiguration, setIdConfiguration] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const [filterOpen, setFilterOpen] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [searchNumber, setSearchNumber] = useState('');

    // Stany okna edycji / dodawania
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [archived, setArchived] = useState(null);
    const [isDriver, setIsDriver] = useState(null);
    const [idProfiles, setIdProfiles] = useState('');
    const [driversList, setDriversList] = useState([]);

    // Stany dla Stanowisk Pracy (Device Stands)
    const [availableStands, setAvailableStands] = useState([]); // lista stanowisk z nazwami maszyn
    const [selectedStandIds, setSelectedStandIds] = useState([]); // wybrane stanowiska ID
    const [standBonusRates, setStandBonusRates] = useState({}); // { [standId]: '10' } stawka dodatku per stanowisko

    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [isScanning, setIsScanning] = useState(false);

    // Snackbar
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);


    // Pobieranie kierowców
    useEffect(() => {
        if (isDriver === 1 && idConfiguration) {
            fetchDrivers();
        }
    }, [isDriver, idConfiguration]);

    const fetchDrivers = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, username, full_name')
                .eq('id_configuration', idConfiguration)
                .eq('profile_type', 'driver');

            if (error) throw error;
            setDriversList(data || []);
        } catch (err) {
            console.error('Błąd podczas pobierania kierowców:', err);
        }
    };

    // Pobieranie maszyn ze stanowiskami dla słownika wyboru
    const fetchAvailableStands = async () => {
        if (!idConfiguration) return;

        // Pobieramy stanowiska dołączając nazwę maszyny
        const { data, error } = await supabase
            .from('device_stands')
            .select('id, name, device_id, is_operator_role, devices(name)')
            .eq('id_configuration', idConfiguration);

        if (error) {
            console.error('Błąd pobierania stanowisk:', error);
            return;
        }

        const formatted = (data || []).map((item) => ({
            id: item.id,
            standName: item.name,
            deviceName: item.devices?.name || 'Nieznana maszyna',
            label: `${item.devices?.name || 'Maszyna'} - ${item.name}`,
            isOperatorRole: item.is_operator_role,
        }));

        setAvailableStands(formatted);
    };

    async function fetchWorkers() {
        if (!idConfiguration) return;
        setLoading(true);

        let query = supabase
            .from('employees')
            .select('*')
            .eq('id_configuration', idConfiguration);

        if (showArchived) {
            query = query.eq('archived', 1);
        } else {
            query = query.is('archived', null);
        }

        const { data, error } = await query.order('id', { ascending: false });

        if (error) {
            console.error('Błąd pobierania pracowników:', error);
            setLoading(false);
            return;
        }

        setRows(data || []);
        setLoading(false);
    }

    useEffect(() => {
        if (idConfiguration) {
            fetchWorkers();
            fetchAvailableStands();
        }
    }, [idConfiguration, showArchived]);

    const filteredRows = rows.filter((row) => {
        const fullName = `${row.name ?? ''} ${row.surname ?? ''}`.toLowerCase();
        const matchesName = searchName === '' || fullName.includes(searchName.toLowerCase());
        const matchesNumber = searchNumber === '' || row.id?.toString().includes(searchNumber);
        return matchesName && matchesNumber;
    });

    const handleScanNfc = async () => {
        if (!('NDEFReader' in window)) {
            alert("Twoja przeglądarka nie obsługuje NFC. Użyj Chrome na Androidzie.");
            return;
        }

        try {
            setIsScanning(true);
            const ndef = new window.NDEFReader();
            await ndef.scan();

            ndef.onreading = (event) => {
                const serialNumber = event.serialNumber;
                setForm((prev) => ({ ...prev, nfc_card_id: serialNumber }));
                setIsScanning(false);
            };

            ndef.onreadingerror = () => {
                alert("Nie udało się odczytać taga. Spróbuj ponownie.");
                setIsScanning(false);
            };

        } catch (error) {
            console.error("Błąd NFC:", error);
            setIsScanning(false);
            alert("Błąd połączenia z czytnikiem NFC. Upewnij się, że NFC jest włączone w telefonie.");
        }
    };

    const fetchEmployeeStands = async (employeeId) => {
        const { data, error } = await supabase
            .from('employee_device_stands')
            .select('device_stand_id, bonus_rate')
            .eq('employee_id', employeeId);

        if (!error && data) {
            setSelectedStandIds(data.map((item) => item.device_stand_id));
            const rates = {};
            data.forEach((item) => {
                rates[item.device_stand_id] = item.bonus_rate != null ? String(item.bonus_rate) : '';
            });
            setStandBonusRates(rates);
        } else {
            setSelectedStandIds([]);
            setStandBonusRates({});
        }
    };

    const openAddDialog = () => {
        setEditingId(null);
        setForm(emptyForm);
        setArchived(null);
        setIsDriver(null);
        setIdProfiles('');
        setSelectedStandIds([]);
        setStandBonusRates({});
        setErrorMsg('');
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditingId(row.id);
        setForm({
            name: row.name ?? '',
            surname: row.surname ?? '',
            position: row.position ?? '',
            contract_type: row.contract_type ?? '',
            hourly_rate: row.hourly_rate !== null && row.hourly_rate !== undefined ? String(row.hourly_rate) : '',
            pin: row.pin ?? '',
            nfc_card_id: row.nfc_card_id ?? '',
        });
        setArchived(row.archived || null);
        setIsDriver(row.isDriver || null);
        setIdProfiles(row.id_profiles || '');
        setErrorMsg('');

        fetchEmployeeStands(row.id);
        setDialogOpen(true);
    };

    const handleFieldChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleStandsChange = (event) => {
        const { target: { value } } = event;
        const newIds = typeof value === 'string' ? value.split(',') : value;
        setSelectedStandIds(newIds);
        setStandBonusRates((prev) => {
            const next = {};
            newIds.forEach((id) => { next[id] = prev[id] ?? ''; });
            return next;
        });
    };

    const handleStandRateChange = (standId) => (e) => {
        setStandBonusRates((prev) => ({ ...prev, [standId]: e.target.value }));
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.surname.trim() || !form.pin.trim()) {
            setErrorMsg('Imię, nazwisko oraz PIN są wymagane.');
            return;
        }

        if (!idConfiguration) {
            setErrorMsg('Nie udało się ustalić konfiguracji użytkownika.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const computedIsOperator = selectedStandIds.some(
            (id) => availableStands.find((s) => s.id === id)?.isOperatorRole
        ) ? 1 : null;

        const payload = {
            name: form.name.trim(),
            surname: form.surname.trim(),
            position: form.position.trim(),
            contract_type: form.contract_type.trim(),
            hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
            pin: form.pin.trim(),
            nfc_card_id: form.nfc_card_id.trim() || null,
            archived: archived,
            isOperator: computedIsOperator,
            isDriver: isDriver,
            id_profiles: isDriver === 1 ? (idProfiles || null) : null,
        };

        let targetEmployeeId = editingId;

        if (editingId) {
            const { error } = await supabase.from('employees').update(payload).eq('id', editingId);
            if (error) {
                console.error('Błąd aktualizacji pracownika:', error);
                if (error.code === '23505') {
                    setErrorMsg("Ten PIN jest już zajęty przez innego pracownika.");
                } else {
                    setErrorMsg(`Wystąpił błąd zapisu: ${error.message}`);
                }
                setSaving(false);
                return;
            }
        } else {
            const { data, error } = await supabase
                .from('employees')
                .insert([{ ...payload, id_configuration: idConfiguration }])
                .select()
                .single();

            if (error) {
                console.error('Błąd dodawania pracownika:', error);
                if (error.code === '23505') {
                    setErrorMsg("Ten PIN jest już przypisany do innego pracownika.");
                } else {
                    setErrorMsg(`Wystąpił błąd: ${error.message}`);
                }
                setSaving(false);
                return;
            }
            if (data) {
                targetEmployeeId = data.id;
            }
        }

        // Zapis relacji ze stanowiskami (jeżeli nie jest kierowcą)
        if (targetEmployeeId) {
            // Usuń obecne stanowiska pracownika
            await supabase.from('employee_device_stands').delete().eq('employee_id', targetEmployeeId);

            // Jeśli nie jest kierowcą i zaznaczono stanowiska - zapisz je
            if (isDriver !== 1 && selectedStandIds.length > 0) {
                const standsPayload = selectedStandIds.map((standId) => ({
                    employee_id: targetEmployeeId,
                    device_stand_id: standId,
                    id_configuration: idConfiguration,
                    bonus_rate: standBonusRates[standId] ? parseFloat(standBonusRates[standId]) : null,
                }));

                const { error: standsError } = await supabase.from('employee_device_stands').insert(standsPayload);
                if (standsError) {
                    console.error('Błąd podczas zapisywania stanowisk pracownika:', standsError);
                }
            }
        }

        setSaving(false);
        setSnackbarMessage(editingId ? "Dane zostały pomyślnie zaktualizowane!" : "Pracownik został pomyślnie dodany do bazy!");
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setDialogOpen(false);
        fetchWorkers();
    };

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.6, minWidth: 80, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'name', headerName: 'IMIĘ', flex: 1, minWidth: 130 },
        { field: 'surname', headerName: 'NAZWISKO', flex: 1.2, minWidth: 150, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'nfc_card_id', headerName: 'NFC', flex: 1, minWidth: 130 },
        {
            field: 'archived',
            headerName: 'STATUS',
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value === 1 ? 'Nieaktywny' : 'Aktywny'}
                    size="small"
                    sx={{
                        bgcolor: params.value === 1 ? '#F1EDE6' : '#E4EDFB',
                        color: params.value === 1 ? '#6B6B6B' : '#2E6DC4',
                        fontWeight: 600,
                        borderRadius: '8px',
                        height: 26
                    }}
                />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'primary.main' }}>Pracownicy</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setFilterOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2, py: 1 }}
                    >
                        Filtry
                    </Button>
                    <Button
                        variant="contained"
                        onClick={openAddDialog}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                    >
                        + Nowy pracownik
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    onRowClick={(params) => openEditDialog(params.row)}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooter={filteredRows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            {/* Dialog Dodawania / Edycji Pracownika */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        {editingId ? 'Edycja pracownika' : 'Dodaj pracownika'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="Imię"
                            size="small"
                            value={form.name}
                            onChange={handleFieldChange('name')}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Nazwisko"
                            size="small"
                            value={form.surname}
                            onChange={handleFieldChange('surname')}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Stanowisko opisowe"
                            size="small"
                            value={form.position}
                            onChange={handleFieldChange('position')}
                            fullWidth
                        />
                        <TextField
                            label="Typ umowy"
                            size="small"
                            value={form.contract_type}
                            onChange={handleFieldChange('contract_type')}
                            fullWidth
                        />
                        <TextField
                            label="Stawka godzinowa"
                            size="small"
                            type="number"
                            value={form.hourly_rate}
                            onChange={handleFieldChange('hourly_rate')}
                            fullWidth
                        />
                        <TextField
                            label="PIN"
                            size="small"
                            value={form.pin}
                            onChange={handleFieldChange('pin')}
                            fullWidth
                            required
                        />

                        {/* Odczyt karty NFC */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label="ID Karty NFC"
                                size="small"
                                value={form.nfc_card_id}
                                onChange={handleFieldChange('nfc_card_id')}
                                fullWidth
                            />
                            <Button
                                variant="outlined"
                                onClick={handleScanNfc}
                                disabled={isScanning}
                                startIcon={isScanning ? <CircularProgress size={16} /> : <NfcIcon />}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: '8px' }}
                            >
                                {isScanning ? 'Skanuję...' : 'Zeskanuj'}
                            </Button>
                        </Box>

                        {/* Checkbox Kierowca */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isDriver === 1}
                                    onChange={(e) => setIsDriver(e.target.checked ? 1 : null)}
                                />
                            }
                            label="Pracownik jest kierowcą"
                        />

                        {/* Wybór konta kierowcy (jeśli zaznaczony) */}
                        {isDriver === 1 && (
                            <FormControl size="small" fullWidth>
                                <InputLabel>Przypisane konto kierowcy</InputLabel>
                                <Select
                                    value={idProfiles}
                                    label="Przypisane konto kierowcy"
                                    onChange={(e) => setIdProfiles(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Brak</em>
                                    </MenuItem>
                                    {driversList.map((driver) => (
                                        <MenuItem key={driver.id} value={driver.id}>
                                            {driver.full_name || driver.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* SEKCJA: Stanowiska pracy na maszynach (Multi-Select) */}
                        {isDriver === 1 ? (
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#FAF7F2', border: '1px solid #ECE7DE', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    ℹ️ Kierowca nie ma możliwości wyboru stanowisk pracy na maszynach.
                                </Typography>
                            </Paper>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="stands-select-label">Dozwolone stanowiska na maszynach</InputLabel>
                                <Select
                                    labelId="stands-select-label"
                                    multiple
                                    value={selectedStandIds}
                                    onChange={handleStandsChange}
                                    input={<OutlinedInput label="Dozwolone stanowiska na maszynach" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((id) => {
                                                const stand = availableStands.find((s) => s.id === id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={stand ? stand.label : id}
                                                        size="small"
                                                        sx={{ borderRadius: '6px', fontSize: '12px' }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {availableStands.map((stand) => (
                                        <MenuItem key={stand.id} value={stand.id}>
                                            <Checkbox checked={selectedStandIds.indexOf(stand.id) > -1} />
                                            <ListItemText primary={stand.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {isDriver !== 1 && selectedStandIds.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Stawka dodatku za pracę na danym stanowisku
                                </Typography>
                                {selectedStandIds.map((standId) => {
                                    const stand = availableStands.find((s) => s.id === standId);
                                    return (
                                        <Box key={standId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ flex: 1, fontSize: 14 }}>{stand ? stand.label : standId}</Typography>
                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="0.00"
                                                value={standBonusRates[standId] ?? ''}
                                                onChange={handleStandRateChange(standId)}
                                                sx={{ width: 120 }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {editingId && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={archived === 1}
                                        onChange={(e) => setArchived(e.target.checked ? 1 : null)}
                                        color="error"
                                    />
                                }
                                label="Pracownik archiwalny"
                            />
                        )}

                        {errorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px' }}>{errorMsg}</Typography>}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Dialog Filtrów */}
            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Filtry
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="ID"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchNumber}
                            onChange={(e) => setSearchNumber(e.target.value)}
                        />
                        <TextField
                            label="Imię i nazwisko"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showArchived}
                                    onChange={(e) => setShowArchived(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={showArchived ? 'Pokaż nieaktywnych' : 'Pokaż aktywnych'}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zamknij
                        </Button>
                        <Button variant="contained" onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zastosuj
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Powiadomienia Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ManagerFilesWorker;